import { FC, Dispatch, SetStateAction } from 'react';

import {
  Header,
  Button,
  SpaceBetween,
  Table,
  Flashbar,
  Box,
  TextFilter,
  Pagination
} from '@awsui/components-react';
import { useCollection } from '@awsui/collection-hooks';

import { FlashbarProps } from '@awsui/components-react';
import { IBundle } from '../../../../../interfaces/workSpaces/bundle';

import EditWorkSpaceBundleForm from '../editWorkSpaceBundleForm';
import ChildModal from '../../../../common/ChildModal';

interface ViewProps {
  bundles: IBundle[];
  selectedBundles: IBundle[];
  flashBarItems: FlashbarProps.MessageDefinition[];
  setFlashBarItems: Dispatch<SetStateAction<FlashbarProps.MessageDefinition[]>>;
  disableActionButtons: boolean;
  handleTableRowSelection: (bundle: IBundle[]) => void;
  bundlesIsEmpty: () => boolean;
  tableIsLoading: boolean;
  editBundleFormModalIsVisible: boolean;
  setEditBundleFormModalIsVisible: Dispatch<SetStateAction<boolean>>;
  refreshBundleTable: () => void;
}

const View: FC<ViewProps> = ({
  bundles,
  selectedBundles,
  flashBarItems,
  setFlashBarItems,
  handleTableRowSelection,
  disableActionButtons,
  bundlesIsEmpty,
  tableIsLoading,
  editBundleFormModalIsVisible,
  setEditBundleFormModalIsVisible,
  refreshBundleTable
}) => {
  const renderEmptyTableContent = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No WS Bundles</b>
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          No WS Bundles to display.
        </Box>
      </Box>
    );
  };
  const renderEmptyTableContentAfterFiltering = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No matching WS Bundles</b>
      </Box>
    );
  };

  const {
    items,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps
  } = useCollection(bundles, {
    filtering: {
      empty: renderEmptyTableContent(),
      noMatch: renderEmptyTableContentAfterFiltering(),
      fields: [
        'bundleId',
        'bundleName',
        'imageId',
        'computeTypeName',
        'description'
      ]
    },
    pagination: { pageSize: 10 },
    sorting: {},
    selection: {}
  });
  const { selectedItems: filteredBundles = [] } = collectionProps;

  const renderTableActions = () => {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="link"
            onClick={() => refreshBundleTable()}
          />

          <Button
            iconName="settings"
            disabled={disableActionButtons}
            onClick={() => {
              setEditBundleFormModalIsVisible(true);
            }}
          >
            Edit
          </Button>
        </SpaceBetween>
      </>
    );
  };
  const renderTableHeader = () => {
    return (
      <Header
        counter={
          filteredBundles.length
            ? `(${filteredBundles.length}/${bundles.length})`
            : `(${bundles.length})`
        }
        actions={renderTableActions()}
      >
        WS Bundles
      </Header>
    );
  };

  const renderEditBundleFormModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setEditBundleFormModalIsVisible(false);
        }}
        visible={editBundleFormModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Edit WS Bundle"
      >
        <EditWorkSpaceBundleForm
          refreshBundleTable={refreshBundleTable}
          bundles={selectedBundles}
        />
      </ChildModal>
    );
  };

  const tableColumnDefinitions = [
    {
      id: 'bundleId',
      header: 'Bundle ID',
      cell: (item: IBundle) => `${item.bundleId} (${item.bundleName})` || '-'
    },
    {
      id: 'uiName',
      header: 'Option Name',
      cell: (item: IBundle) => item.uiName || '-'
    },
    {
      id: 'uiDescription',
      header: 'Option Description',
      cell: (item: IBundle) => item.uiDescription || '-'
    },
    {
      id: 'computeTypeName',
      header: 'Compute Type',
      cell: (item: IBundle) => item.computeTypeName || '-'
    },
    {
      id: 'imageId',
      header: 'Image ID',
      cell: (item: IBundle) => item.imageId || '-'
    },
    {
      id: 'rootStorageCapacity',
      header: 'Root Storage Capacity',
      cell: (item: IBundle) => item.rootStorageCapacity || '-'
    }
  ];
  return (
    <>
      <SpaceBetween direction="vertical" size="xs">
        <Flashbar items={flashBarItems} />
        <Table
          {...collectionProps}
          loading={tableIsLoading}
          trackBy="bundleId"
          selectedItems={selectedBundles}
          selectionType="single"
          columnDefinitions={tableColumnDefinitions}
          items={items}
          loadingText="Loading WS Bundles"
          onSelectionChange={({ detail }) =>
            handleTableRowSelection(detail.selectedItems)
          }
          header={renderTableHeader()}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} matches`}
              filteringAriaLabel="Filter WS Bundles"
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`
              }}
            />
          }
        />
      </SpaceBetween>
      {renderEditBundleFormModal()}
    </>
  );
};
export default View;
